import { Injectable } from '@angular/core';
import { AbstractFeatureQueryEntity } from '@y42/feature';
import { CustomTheme, ThemeData, ThemeType, Y42FeatureType } from '@y42/json-schema';
import { combineLatest, distinctUntilChanged, map, shareReplay } from 'rxjs';
import { builtInThemes } from './built-in-themes.constant';
import { CustomThemeWithId, VisualizationThemeState, VisualizationThemeUIModel } from './visualization-theme.model';
import { VisualizationThemeStore } from './visualization-theme.store';

@Injectable({ providedIn: 'root' })
export class VisualizationThemeQuery extends AbstractFeatureQueryEntity<
  VisualizationThemeState,
  VisualizationThemeUIModel
> {
  constructor(protected readonly store: VisualizationThemeStore) {
    super({
      store,
      featureType: Y42FeatureType.VisualizationTheme,
      toUIModel: (storeModel) => ({
        featureType: Y42FeatureType.VisualizationTheme,
        ...storeModel,
        ...storeModel.settings,
        theme: storeModel.settings.theme,
      }),
    });
  }

  readonly defaultTheme = builtInThemes[4]!.theme;

  readonly active$ = this.selectActive().pipe(
    map((model) => (model ? this.toUIModel(model) : undefined)),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  readonly localThemeChanges$ = this.select((state) => state.ui.localThemeChanges);

  readonly activeThemeWithLocalChanges$ = combineLatest([this.active$, this.localThemeChanges$]).pipe(
    map(([active, changes]) => getThemeData(active, changes)),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  readonly isCustomThemeActive$ = this.active$.pipe(map((active) => active?.theme?.type === ThemeType.Custom));

  readonly hasLocalChanges$ = this.localThemeChanges$.pipe(
    map((changes) => Object.keys(changes).length > 0),
    distinctUntilChanged(),
  );

  readonly items$ = this.selectAll().pipe(map((models) => models.map((model) => this.toUIModel(model))));

  readonly customThemes$ = this.items$.pipe(
    map((models) => models.filter((model) => model.theme.type === ThemeType.Custom)),
    map<VisualizationThemeUIModel[], CustomThemeWithId[]>((models) =>
      models.map((model) => {
        const theme = model.theme as CustomTheme;
        const isActive = model.$path === this.getActive()?.$path;

        if (isActive) {
          return { ...theme, ...this.store.getValue().ui.localThemeChanges, id: model.$path };
        }

        return { ...theme, id: model.$path };
      }),
    ),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  readonly activeSection$ = this.select((state) => state.ui.activeSection);

  readonly toListViewModel = (item: VisualizationThemeUIModel) => [item];

  getActiveThemeWithLocalChanges() {
    return getThemeData(this.getActiveUIModel(), this.store.getValue().ui.localThemeChanges);
  }

  getThemeDataByID(themeId: string): ThemeData | undefined {
    const theme = this.getUIEntity(themeId)?.theme;
    if (theme?.type === ThemeType.Custom) {
      return theme;
    }
    return builtInThemes.find((t) => t.theme.themeName === theme?.themeName)?.theme;
  }
}

function getThemeData(
  model: VisualizationThemeUIModel | undefined,
  changes: Partial<ThemeData>,
): (ThemeData & { id?: string }) | undefined {
  if (!model) {
    return undefined;
  }

  const theme = model.theme;

  if (theme.type === ThemeType.Custom) {
    return { ...theme, ...changes, id: model.$path };
  }

  return {
    ...builtInThemes.find((t) => t.theme.themeName === theme.themeName)!.theme,
    ...changes,
  };
}
